import { useCallback, useRef } from 'react';
import { DateRange, TimelineItemWithDisplay } from '../../../models/timeline-calendar.types';

interface TimelineMinimapProps {
  fullDateRange: DateRange;
  dateRange: DateRange;
  itemsWithColors: TimelineItemWithDisplay[];
  hiddenSectionIds: Set<string>;
  onViewRangeChange: (range: DateRange) => void;
  onZoom: (scale: number) => void;
}

const MINIMAP_WIDTH = 200;
const MINIMAP_HEIGHT = 48;

const calculateDistance = (touches: React.TouchList): number => {
  const first = touches[0];
  const second = touches[1];
  const dx = second.clientX - first.clientX;
  const dy = second.clientY - first.clientY;
  return Math.hypot(dx, dy);
};

export const TimelineMinimap = ({
  fullDateRange,
  dateRange,
  itemsWithColors,
  hiddenSectionIds,
  onViewRangeChange,
  onZoom,
}: TimelineMinimapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const pinchDistanceRef = useRef<number | null>(null);

  const fullStartMs = fullDateRange.start.getTime();
  const fullEndMs = fullDateRange.end.getTime();
  const fullSpanMs = Math.max(fullEndMs - fullStartMs, 1);

  // Add padding (10% on each side) so viewport window is visible even at edges
  const padding = fullSpanMs * 0.1;
  const paddedStartMs = fullStartMs - padding;
  const paddedEndMs = fullEndMs + padding;
  const paddedSpanMs = paddedEndMs - paddedStartMs;

  const viewStartMs = dateRange.start.getTime();
  const viewEndMs = dateRange.end.getTime();

  // Viewport indicator position within padded range
  const viewLeftPct = Math.max(0, ((viewStartMs - paddedStartMs) / paddedSpanMs) * 100);
  const viewWidthPct = Math.min(100 - viewLeftPct, ((viewEndMs - viewStartMs) / paddedSpanMs) * 100);

  const navigateToPosition = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    const viewSpanMs = viewEndMs - viewStartMs;
    const centerMs = paddedStartMs + ratio * paddedSpanMs;
    const newStart = centerMs - viewSpanMs / 2;
    const newEnd = centerMs + viewSpanMs / 2;

    onViewRangeChange({
      start: new Date(newStart),
      end: new Date(newEnd),
    });
  }, [viewEndMs, viewStartMs, paddedStartMs, paddedSpanMs, onViewRangeChange]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    navigateToPosition(e.clientX);
  }, [navigateToPosition]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    navigateToPosition(e.clientX);
  }, [navigateToPosition]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    isDraggingRef.current = false;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      const scale = Math.exp(-e.deltaY * 0.01);
      onZoom(scale);
    }
  }, [onZoom]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchDistanceRef.current = calculateDistance(e.touches);
    } else {
      pinchDistanceRef.current = null;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 2 || pinchDistanceRef.current === null) return;
    e.preventDefault();
    e.stopPropagation();
    const currentDistance = calculateDistance(e.touches);
    if (currentDistance <= 0 || pinchDistanceRef.current <= 0) return;
    const scale = currentDistance / pinchDistanceRef.current;
    pinchDistanceRef.current = currentDistance;
    onZoom(scale);
  }, [onZoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      pinchDistanceRef.current = null;
    }
  }, []);

  // Group items by section for vertical distribution
  const sectionIds = [...new Set(itemsWithColors.map((item) => item.sectionId).filter(Boolean))];
  const sectionCount = Math.max(sectionIds.length, 1);

  return (
    <div
      className="pointer-events-none fixed bottom-5 left-5 z-40 hidden sm:block"
    >
      <div
        ref={containerRef}
        className="pointer-events-auto relative cursor-pointer overflow-hidden rounded-lg"
        style={{
          width: MINIMAP_WIDTH,
          height: MINIMAP_HEIGHT,
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          touchAction: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Item bars */}
        {itemsWithColors.filter((item) => !item.sectionId || !hiddenSectionIds.has(item.sectionId)).map((item) => {
          const itemStartMs = new Date(item.startDate).getTime();
          const itemEndMs = item.endDate
            ? new Date(item.endDate).getTime()
            : itemStartMs;

          const leftPct = ((itemStartMs - paddedStartMs) / paddedSpanMs) * 100;
          const widthPct = Math.max(
            ((itemEndMs - itemStartMs) / paddedSpanMs) * 100,
            0.5, // minimum visible width
          );

          if (leftPct + widthPct < 0 || leftPct > 100) return null;

          const sectionIndex = item.sectionId ? sectionIds.indexOf(item.sectionId) : 0;
          const sectionHeight = (MINIMAP_HEIGHT - 4) / sectionCount; // 4px for padding
          const top = 2 + sectionIndex * sectionHeight + sectionHeight * 0.3;
          const barHeight = Math.max(sectionHeight * 0.4, 2);

          const isPoint = !item.endDate;

          return (
            <div
              key={item.id}
              className="absolute"
              style={{
                left: `${Math.max(0, leftPct)}%`,
                width: isPoint ? '3px' : `${Math.max(widthPct, 0.4)}%`,
                top,
                height: isPoint ? '3px' : barHeight,
                backgroundColor: item.color?.bg ?? '#3b82f6',
                opacity: 0.6,
                borderRadius: isPoint ? '50%' : '1px',
              }}
            />
          );
        })}

        {/* Today indicator */}
        {(() => {
          const nowMs = Date.now();
          const todayPct = ((nowMs - paddedStartMs) / paddedSpanMs) * 100;
          if (todayPct < 0 || todayPct > 100) return null;
          return (
            <div
              className="absolute inset-y-0"
              style={{
                left: `${todayPct}%`,
                width: '1.5px',
                backgroundColor: '#ef4444',
              }}
            />
          );
        })()}

        {/* Viewport indicator */}
        <div
          className="absolute inset-y-0"
          style={{
            left: `${viewLeftPct}%`,
            width: `${Math.max(viewWidthPct, 1)}%`,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderLeft: '1.5px solid rgba(255, 255, 255, 0.5)',
            borderRight: '1.5px solid rgba(255, 255, 255, 0.5)',
          }}
        />
      </div>
    </div>
  );
};
